import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import OneVerseLogo from '@/components/OneVerseLogo';

/**
 * 404 page. Previously the router's catch-all redirected to /login, which made a
 * mistyped or stale URL indistinguishable from an expired session for a user who
 * was still signed in.
 *
 * Rendered outside the authenticated layout chain so it works for signed-out
 * visitors too — hence "Dashboard" is a plain link rather than a gated route
 * push; ProtectedRoute handles sending an unauthenticated visitor on to login.
 */
const NotFound: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // history.length is 1 on a cold load (pasted link, new tab), where going
  // "back" would leave the app entirely.
  const canGoBack = window.history.length > 1;

  return (
    <main className="min-h-screen bg-white flex items-center justify-center px-4 py-24">
      <div className="relative w-full max-w-2xl flex flex-col items-center text-center">
        {/* Oversized watermark from the design. Purely decorative: hidden from
            assistive tech, and it must not intercept clicks on the buttons. */}
        <span
          aria-hidden="true"
          className="pointer-events-none select-none absolute inset-0 flex items-center justify-center font-semibold text-gray-100 leading-none text-[180px] sm:text-[280px] lg:text-[340px]"
        >
          404
        </span>

        <div className="relative flex flex-col items-center gap-6">
          <OneVerseLogo className="w-44" />

          <div className="flex flex-col gap-3">
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 text-balance">
              {t('errors.not_found_title')}
            </h1>
            <p className="text-sm sm:text-base text-gray-600 max-w-md">
              {t('errors.not_found_body')}
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            {canGoBack && (
              <Button variant="outline" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-4 h-4" />
                {t('errors.go_back')}
              </Button>
            )}
            <Button asChild>
              <Link to="/dashboard">{t('nav.dashboard')}</Link>
            </Button>
          </div>
        </div>
      </div>
    </main>
  );
};

export default NotFound;
